import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router";
import queryKeys from "../configs/react-query/query-key";
import { normalizeResponseData } from "../utils/common";
import { useMutationData } from "./useMutationData";
import { getExternalApiErrorMessage, getVerificationUploadErrorMessage } from "@utils/error";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { useTranslation } from "react-i18next";
export const useClarityVerificationRequest = () => {
  const { t } = useTranslation();
  const { projectSeq } = useParams();
  const [verificationData, setVerificationData] = useState([]);
  const isMountedRef = useRef(true);
  const pollTimersBySeqRef = useRef(new Map());
  const clearPollTimerForSeq = (seq) => {
    const key = String(seq);
    const id = pollTimersBySeqRef.current.get(key);
    if (id != null) {
      clearTimeout(id);
      pollTimersBySeqRef.current.delete(key);
    }
  };

  const cleanup = () => {
    isMountedRef.current = false;
    pollTimersBySeqRef.current.forEach((id) => clearTimeout(id));
    pollTimersBySeqRef.current.clear();
  };

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const checkDuplicateVerification = useMutationData(
    queryKeys.checkDuplicateVerificationUpload,
    {
      method: "POST",
      params: {
        projectId: projectSeq,
        verificationType: "clarity",
      },
    },
    {
      onError: (error) => {
        console.error("Error during checksum verification:", error);
      },
    },
  );

  const handleSubmitVerificationRequest = async (data, setProcessingRowId, onDuplicateFound) => {
    try {
      // Check file size (1GB = 1024 * 1024 * 1024 bytes)
      const maxFileSize = 1024 * 1024 * 1024; // 1GB in bytes

      if (data.verifFile[0].size > maxFileSize) {
        showErrorToast(t("common.error"), t("project.verification.file-size-exceeded"));
        return;
      }

      // First check for duplicate file
      const checksumFormData = new FormData();
      checksumFormData.append("file", data.verifFile[0]);

      const response = await checkDuplicateVerification.mutateAsync(checksumFormData);
      const duplicatedSeq = response?.duplicateSeq || 0;
      if (duplicatedSeq > 0) {
        // Call the callback to show confirmation modal with both options
        if (onDuplicateFound) {
          onDuplicateFound(
            () => proceedWithVerification(data, setProcessingRowId, duplicatedSeq, response.fileName, false),
            () => proceedWithVerification(data, setProcessingRowId, 0, null, true)
          );
          return;
        }
      }

      await proceedWithVerification(data, setProcessingRowId, 0, null, false);
    } catch (error) {
      console.error("Error during verification request:", error);
    }
  };

  // New function for re-upload that creates a new verification record instead of updating existing one
  const handleReUploadVerification = async (data, onDuplicateFound, onSuccess, setProcessingRowId) => {
    const { requestComment, projectSeq, projectId, verifFile, scanTypeCd } = data;

    try {
      // Check file size (1GB = 1024 * 1024 * 1024 bytes)
      const maxFileSize = 1024 * 1024 * 1024; // 1GB in bytes

      if (verifFile[0].size > maxFileSize) {
        showErrorToast(t("common.error"), t("project.verification.file-size-exceeded"));
        return;
      }

      // First check for duplicate file (same as original upload)
      const checksumFormData = new FormData();
      checksumFormData.append("file", verifFile[0]);

      const response = await checkDuplicateVerification.mutateAsync(checksumFormData);
      const duplicatedSeq = response?.duplicateSeq || 0;
      if (duplicatedSeq > 0) {
        // Call the callback to show confirmation modal with both options
        if (onDuplicateFound) {
          onDuplicateFound(
            () => proceedWithReUpload(data, onSuccess, setProcessingRowId, duplicatedSeq, response.fileName, false),
            () => proceedWithReUpload(data, onSuccess, setProcessingRowId, 0, null, true)
          );
          return;
        }
      }

      // If no duplicate or no callback provided, proceed with upload
      await proceedWithReUpload(data, onSuccess, setProcessingRowId, 0, null, false);
    } catch (error) {
      throw error;
    }
  };

  const proceedWithReUpload = async (data, onSuccess, setProcessingRowId, duplicatedSeq = 0, fileName = null, forceNewScan = false) => {
    const { requestComment, projectSeq, projectId, verifFile, scanTypeCd } = data;

    try {
      // If forceNewScan is true, always perform new scan regardless of duplicate
      const effectiveDuplicatedSeq = forceNewScan ? 0 : duplicatedSeq;
      const isDuplicateWithSameName = !forceNewScan && fileName !== null && fileName === verifFile[0].name && duplicatedSeq > 0;
      
      // Create new verification record instead of updating existing one
      const response = await createBinaryVerification.mutateAsync({
        projectSeq,
        projectId,
        requestComment: requestComment || "",
        scanTypeCd,
        fulfillmentFlag: "N",
        duplicatedSeq: effectiveDuplicatedSeq,
        upload: forceNewScan ? "true" : (isDuplicateWithSameName ? "false" : "true"),
        repositoryImportFlag: data.repositoryImportFlag || "N",
      });

      if (typeof response !== "string") {
        // Upload new file using the new verification record
        const formData = new FormData();
        let binaryVerificationSeq = response?.data?.binaryVerificationSeq;
        formData.append("binaryFile", verifFile[0]);
        formData.append("binaryVerificationSeq", binaryVerificationSeq);
        formData.append("isScan", forceNewScan ? "true" : (effectiveDuplicatedSeq === 0 ? "true" : "false"));


        try {
          if (forceNewScan || !isDuplicateWithSameName) {
             await verificationUpload.mutateAsync(formData);
          }
        } catch (error) {
          console.error("Upload failed:", error);
        }

        // Set processing state briefly (no polling)
        if (setProcessingRowId) {
          setProcessingRowId(binaryVerificationSeq);
        }

        // Refresh verification data once
        await getVerificationData.mutateAsync({ projectSeq: projectSeq });

        // Clear processing state immediately
        if (setProcessingRowId) {
          setProcessingRowId(null);
        }

        // Call success callback if provided (this will handle navigation)
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const proceedWithVerification = async (data, setProcessingRowId, duplicatedSeq = 0, fileName = null, forceNewScan = false) => {
    const { projectSeq, projectId, requestComment, scanTypeCd } = data;

    try {
      // If forceNewScan is true, always perform new scan regardless of duplicate
      const effectiveDuplicatedSeq = forceNewScan ? 0 : duplicatedSeq;
      const isDuplicateWithSameName = !forceNewScan && fileName !== null && fileName === data.verifFile[0].name && duplicatedSeq > 0;
      
      const response = await createBinaryVerification.mutateAsync({
        projectSeq,
        projectId,
        requestComment,
        scanTypeCd,
        fulfillmentFlag: "N",
        repositoryImportFlag: data.repositoryImportFlag || "N",
        duplicatedSeq: effectiveDuplicatedSeq,
        upload: forceNewScan ? "true" : (isDuplicateWithSameName ? "false" : "true"),
      });

      if (typeof response !== "string") {
        const formData = new FormData();
        let binaryVerificationSeq = response?.data?.binaryVerificationSeq;
        formData.append("binaryFile", data.verifFile[0]);
        formData.append("binaryVerificationSeq", binaryVerificationSeq);
        formData.append("isScan", forceNewScan ? "true" : (effectiveDuplicatedSeq === 0 ? "true" : "false"));

        try {
          if (forceNewScan || !isDuplicateWithSameName) {
              await verificationUpload.mutateAsync(formData);
          }
        } catch (error) {
          console.error("Upload failed:", error);
        }
        setProcessingRowId(binaryVerificationSeq);
        await getVerificationData.mutateAsync({ projectSeq: projectSeq });
        await pollVerificationStatus(binaryVerificationSeq);
        setProcessingRowId(null);
        await getVerificationData.mutateAsync({ projectSeq: projectSeq });
      }
    } catch (error) {
      console.error("Error during verification request:", error);
    }
  };

  const createBinaryVerification = useMutationData(
    queryKeys.createBinaryVerification,
    {
      method: "POST",
      skipErrorToast: true,
    },
    {
      onError: (error) => {
        const errorMessage = getExternalApiErrorMessage(error, t);
        useSuccessToast(TOAST.error, t("common.error"), errorMessage);
      },
    },
  );

  const verificationUpload = useMutationData(
    queryKeys.binaryVerificationUpload,
    {
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
      skipErrorToast: true,
    },
    {
      onError: (error) => {
        const errorMessage = getVerificationUploadErrorMessage(error, t);
        useSuccessToast(TOAST.error, t("common.error"), errorMessage);
      },
    },
  );

  const getVerificationData = useMutationData(
    `${queryKeys.binaryVerificationByProjectSeq}`,
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setVerificationData(normalizeResponseData(response.object));
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const getVerificationStatus = useMutationData(
    (binaryVerificationSeq) => `${queryKeys.binaryVerificationStatus}/${binaryVerificationSeq}`,
    {
      method: "GET",
    },
    {
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const pollVerificationStatus = async (binaryVerificationSeq) => {
    const seqKey = String(binaryVerificationSeq);
    clearPollTimerForSeq(seqKey);

    return new Promise((resolve) => {
      const scheduleNext = () => {
        const id = setTimeout(tick, 5000);
        pollTimersBySeqRef.current.set(seqKey, id);
      };

      const tick = async () => {
        if (!isMountedRef.current) {
          clearPollTimerForSeq(seqKey);
          return;
        }

        try {
          const statusResponse = await getVerificationStatus.mutateAsync(binaryVerificationSeq);

          if (!statusResponse) {
            if (!isMountedRef.current) {
              clearPollTimerForSeq(seqKey);
              return;
            }
            scheduleNext();
            return;
          }

          const { verificationStatusCd, clarityScanStatusCd, clarityScannedFlag } = statusResponse;

          const isSuccess =
            verificationStatusCd === "ON_VERIFICATION" &&
            clarityScanStatusCd === "success" &&
            clarityScannedFlag === "Y";

          const isAnalysisCheck = verificationStatusCd === "ANALYSIS_CHECK";
          const isError = verificationStatusCd === "ERROR";

          if (isSuccess || isAnalysisCheck || isError) {
            clearPollTimerForSeq(seqKey);
            if (isMountedRef.current) {
              resolve(statusResponse);
            }
            return;
          }
        } catch (error) {
          if (error?.response?.status === 401) {
            clearPollTimerForSeq(seqKey);
            cleanup();
            return;
          }
          console.error("Error polling verification status:", error);
        }

        if (!isMountedRef.current) {
          clearPollTimerForSeq(seqKey);
          return;
        }
        scheduleNext();
      };

      scheduleNext();
    });
  };

  return {
    verificationUpload,
    handleSubmitVerificationRequest,
    handleReUploadVerification,
    verificationData,
    getVerificationData,
    checkDuplicateVerification,
    isDuplicateCheckPending: checkDuplicateVerification?.isPending ?? false,
    cleanup, // Export cleanup function for manual cleanup
  };
};
